// UK postcode helpers for delivery_notes.delivery_postcode (see 20261002_delivery_notes_delivery_postcode.sql).
// The pattern and the "last match wins" rule are the same as the SQL function extract_uk_postcode().

const POSTCODE_RE = /\b([A-Z]{1,2}[0-9][A-Z0-9]?)\s*([0-9][A-Z]{2})\b/g
// What the table's CHECK constraint accepts: 'B76 1AH' or the outward code alone, 'B76'.
const STORED_RE = /^[A-Z]{1,2}[0-9][A-Z0-9]?( [0-9][A-Z]{2})?$/

/** Last UK postcode in a block of address text, as 'B76 1AH'; null if there is none. */
export function extractUkPostcode(text: string | null | undefined): string | null {
  if (!text) return null
  let last: RegExpExecArray | null = null
  for (const m of text.toUpperCase().matchAll(POSTCODE_RE)) last = m
  return last ? `${last[1]} ${last[2]}` : null
}

/** Normalise a hand-typed postcode for storage. Returns null for blank, undefined if it is not a valid shape. */
export function normaliseTypedPostcode(input: string): string | null | undefined {
  const s = input.trim().toUpperCase().replace(/\s+/g, ' ')
  if (!s) return null
  const full = extractUkPostcode(s)
  if (full && full.replace(' ', '') === s.replace(' ', '')) return full
  return STORED_RE.test(s) ? s : undefined
}

interface DestinationFields {
  note_type: string | null | undefined
  fulfilment_type?: string | null
  deliver_to_addr: string | null | undefined
  release_to_addr: string | null | undefined
}

/**
 * Postcode to store for a note being saved. A previously stored value that differs from what the
 * old address parses to was typed by hand and is kept; otherwise it follows the address.
 * Collection-only notes have no delivery destination, so they get null.
 */
export function deliveryPostcodeForSave(
  next: DestinationFields,
  prev?: (DestinationFields & { delivery_postcode?: string | null }) | null,
): string | null {
  const dest = (n: DestinationFields) =>
    n.deliver_to_addr?.trim() ? n.deliver_to_addr : (n.note_type === 'DELIVERY' ? n.release_to_addr : null)
  const parsed = extractUkPostcode(dest(next))
  if (prev?.delivery_postcode && prev.delivery_postcode !== extractUkPostcode(dest(prev))) return prev.delivery_postcode
  return parsed
}
